import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useCollection } from '../../hooks/useFirestore'
import { CATEGORIES } from '../../lib/categories'
import { toDayOfYear } from '../../lib/harptos'
import './AdminEditor.css'

export default function EventList() {
  const { data: events, loading } = useCollection('events')
  const [categoryFilter, setCategoryFilter] = useState(null)
  const [yearFilter, setYearFilter] = useState(null)

  // Extract unique years from events
  const availableYears = useMemo(() => {
    const years = new Set()
    events.forEach(event => {
      if (event.start?.year) years.add(event.start.year)
      if (event.end?.year) years.add(event.end.year)
    })
    return Array.from(years).sort((a, b) => a - b)
  }, [events])

  // Filter and sort events
  const filteredEvents = useMemo(() => {
    let filtered = events

    if (categoryFilter) {
      filtered = filtered.filter(e => e.category === categoryFilter)
    }

    if (yearFilter) {
      filtered = filtered.filter(e =>
        e.start?.year === yearFilter || e.end?.year === yearFilter
      )
    }

    // Sort by year, then by day of year, then by sortOrder for same-day events
    return [...filtered].sort((a, b) => {
      // First, sort by year
      if (a.start?.year !== b.start?.year) {
        return (a.start?.year || 0) - (b.start?.year || 0)
      }
      // Then sort by day of year (using Harptos calendar)
      const aDoy = a.start ? toDayOfYear(a.start.month, a.start.day || 1, a.start.year) : 0
      const bDoy = b.start ? toDayOfYear(b.start.month, b.start.day || 1, b.start.year) : 0
      if (aDoy !== bDoy) {
        return aDoy - bDoy
      }
      // Finally, sort by sortOrder for events on the same day
      return (a.sortOrder || 0) - (b.sortOrder || 0)
    })
  }, [events, categoryFilter, yearFilter])

  const hasFilters = categoryFilter || yearFilter
  const clearFilters = () => {
    setCategoryFilter(null)
    setYearFilter(null)
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  const getCategoryColor = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId)
    return cat?.color || 'var(--dull-steel)'
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>Timeline Events</h1>
        <Link to="/admin/events/new" className="btn btn-primary">
          + New Event
        </Link>
      </div>

      {/* Filters */}
      {events.length > 0 && (
        <div className="admin-filters">
          <div className="filter-group">
            <span className="filter-label">Category:</span>
            <select
              value={categoryFilter || ''}
              onChange={(e) => setCategoryFilter(e.target.value || null)}
            >
              <option value="">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <span className="filter-label">Year:</span>
            <select
              value={yearFilter || ''}
              onChange={(e) => setYearFilter(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">All Years</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year} DR</option>
              ))}
            </select>
          </div>

          {hasFilters && (
            <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
              Clear Filters
            </button>
          )}

          <span className="filter-count">
            {filteredEvents.length} of {events.length} events
          </span>
        </div>
      )}

      {events.length === 0 ? (
        <div className="empty-state">
          <p>No events yet.</p>
          <p className="text-secondary">Create your first timeline event to get started.</p>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="empty-state">
          <p>No events match your filters.</p>
          <button className="btn btn-secondary" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="admin-list">
          {filteredEvents.map(event => {
            const isHidden = event.hidden === true
            return (
              <Link
                key={event.id}
                to={`/admin/events/${event.id}`}
                className={`admin-list-item ${isHidden ? 'hidden-item' : ''}`}
                style={{ opacity: isHidden ? 0.5 : 1 }}
              >
                <div
                  className="event-indicator"
                  style={{ background: getCategoryColor(event.category) }}
                />
                <div className="item-info">
                  <h3>
                    {event.title}
                    {isHidden && <span className="hidden-tag">Hidden</span>}
                  </h3>
                  <p className="text-secondary">
                    {formatHarptosDate(event.start)}
                    {event.end && ` — ${formatHarptosDate(event.end)}`}
                  </p>
                </div>
                <span className="item-badge" style={{
                  background: `color-mix(in srgb, ${getCategoryColor(event.category)} 20%, transparent)`,
                  color: getCategoryColor(event.category)
                }}>
                  {event.category || 'other'}
                </span>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

function formatHarptosDate(date) {
  if (!date) return 'Unknown'
  if (date.day && date.day > 0) {
    return `${date.month} ${date.day}, ${date.year} DR`
  }
  return `${date.month}, ${date.year} DR`
}
